import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Settings as SettingsIcon, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/uiext";
import { PasswordInput } from "@/components/PasswordInput";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import { RecentLoginsPanel, UnverifiedEmailBanner } from "@/components/SecurityPanels";
import { TwoFactorCard } from "@/components/TwoFactorCard";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [oldp, setOldp] = useState("");
  const [newp, setNewp] = useState("");
  const [newp2, setNewp2] = useState("");

  // Phase 4m — "Reset my workspace" is an inline full-page editor,
  // NOT a modal, so the destructive nature is obvious and the operator
  // can't dismiss it with a tap outside.
  const [resetMode, setResetMode] = useState(false);
  const [resetForm, setResetForm] = useState({ password: "", phrase: "" });
  const [resetting, setResetting] = useState(false);

  const orgName = user?.org?.name || "";

  const change = async (e) => {
    e.preventDefault();
    if (newp !== newp2) return toast.error(t("pages.settings.passwordsDontMatch"));
    if (newp.length < 6) return toast.error(t("pages.settings.passwordTooShort"));
    try {
      await api.post("/auth/change-password", { old_password: oldp, new_password: newp });
      toast.success(t("pages.settings.passwordChanged"));
      setOldp("");
      setNewp("");
      setNewp2("");
      setUser({ ...user, must_change_password: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.failed"));
    }
  };

  const runReset = async () => {
    if ((resetForm.phrase || "").trim().toLowerCase() !== orgName.trim().toLowerCase()) {
      return toast.error(
        t(
          "pages.settings.resetPhraseMismatch",
          `Phrase must be exactly "${orgName}"`
        )
      );
    }
    setResetting(true);
    try {
      const r = await api.post("/account/reset", {
        password: resetForm.password,
        confirm_phrase: resetForm.phrase,
      });
      const totalDeleted = Object.values(r.data?.deleted || {}).reduce(
        (a, b) => a + (b || 0),
        0
      );
      toast.success(
        t(
          "pages.settings.resetSuccess",
          `Workspace cleared — ${totalDeleted} records removed. Your login still works.`
        )
      );
      setResetMode(false);
      setResetForm({ password: "", phrase: "" });
      // Full reload so every cached list empties itself.
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.failed"));
    } finally {
      setResetting(false);
    }
  };

  // Phase 4m — inline editor takes over the whole page when reset mode
  // is active so the operator CANNOT dismiss accidentally.
  if (resetMode) {
    const phraseOK =
      (resetForm.phrase || "").trim().toLowerCase() === orgName.trim().toLowerCase();
    return (
      <InlineEditorShell
        crumb={t("pages.settings.title")}
        title={t("pages.settings.resetTitle", "Reset my workspace")}
        onCancel={() => {
          setResetMode(false);
          setResetForm({ password: "", phrase: "" });
        }}
        onSave={runReset}
        saveLabel={
          resetting
            ? t("common.loading", "…")
            : t("pages.settings.resetConfirmBtn", "Yes, wipe my workspace")
        }
        saveDisabled={resetting || !resetForm.password || !phraseOK}
        testidRoot="tenant-reset"
      >
        <div className="space-y-4">
          <div className="rounded-md border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/5 p-4 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-[hsl(var(--danger))] shrink-0 mt-0.5" />
            <div className="space-y-1 text-sm">
              <div className="font-semibold text-[hsl(var(--danger))]">
                {t("pages.settings.resetHeading", "This will permanently delete:")}
              </div>
              <ul className="text-xs list-disc ml-4 space-y-0.5 text-muted-foreground">
                <li>All your customers, resellers and providers</li>
                <li>All trunks, domains, routes and ACLs</li>
                <li>All CDRs, live registrations, call history</li>
                <li>All security events and per-IP bans you created</li>
                <li>All API tokens and notifications</li>
              </ul>
              <div className="text-xs pt-1">
                <span className="font-semibold">Preserved:</span>{" "}
                your login, your workspace record, your billing plan.
                <span className="text-muted-foreground"> Other tenants are not touched.</span>
              </div>
            </div>
          </div>

          <div>
            <Label>{t("pages.settings.currentPassword", "Your password")}</Label>
            <PasswordInput
              value={resetForm.password}
              onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })}
              data-testid="tenant-reset-password"
              autoFocus
            />
          </div>

          <div>
            <Label>
              {t(
                "pages.settings.confirmPhraseLabel",
                "Type your workspace name to confirm:"
              )}{" "}
              <span className="font-mono font-semibold text-[hsl(var(--accent))]">
                {orgName}
              </span>
            </Label>
            <Input
              value={resetForm.phrase}
              onChange={(e) => setResetForm({ ...resetForm, phrase: e.target.value })}
              data-testid="tenant-reset-phrase"
              placeholder={orgName}
              className={
                resetForm.phrase && !phraseOK
                  ? "border-[hsl(var(--danger))]/50"
                  : ""
              }
            />
            {resetForm.phrase && !phraseOK && (
              <p className="text-[11px] text-[hsl(var(--danger))] mt-1">
                {t(
                  "pages.settings.phraseMustMatch",
                  `Must match "${orgName}" exactly (case-insensitive).`
                )}
              </p>
            )}
          </div>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-settings">
      <PageHeader
        title={t("pages.settings.title")}
        subtitle={t("pages.settings.subtitle")}
        icon={SettingsIcon}
      />
      <UnverifiedEmailBanner />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-sm font-medium flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-accent" />{" "}
              {t("pages.settings.adminProfile")}
            </div>
            <div className="text-xs text-muted-foreground">
              {t("pages.settings.username")}:{" "}
              <span className="font-mono text-foreground">{user?.username}</span>
            </div>
            <form onSubmit={change} className="space-y-3">
              <div>
                <Label>{t("pages.settings.oldPassword")}</Label>
                <PasswordInput
                  value={oldp}
                  onChange={(e) => setOldp(e.target.value)}
                  data-testid="old-password"
                  required
                />
              </div>
              <div>
                <Label>{t("pages.settings.newPassword")}</Label>
                <PasswordInput
                  value={newp}
                  onChange={(e) => setNewp(e.target.value)}
                  data-testid="new-password"
                  required
                />
              </div>
              <div>
                <Label>{t("pages.settings.confirmNewPassword")}</Label>
                <PasswordInput
                  value={newp2}
                  onChange={(e) => setNewp2(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" data-testid="change-password-btn">
                {t("pages.settings.changePassword")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Phase 4m — replaced the dead "Seed demo data" card with a
            scoped, clearly-labelled Danger Zone. Affects only this
            tenant, never anyone else. */}
        <Card className="border-[hsl(var(--danger))]/30">
          <CardContent className="p-5 space-y-3">
            <div className="text-sm font-medium flex items-center gap-2 text-[hsl(var(--danger))]">
              <Trash2 className="h-4 w-4" />{" "}
              {t("pages.settings.dangerZone", "Danger zone")}
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                "pages.settings.resetDesc",
                "Clear all entities in your workspace so you can start from scratch. Only your data is affected — other tenants are untouched."
              )}
            </p>
            <Button
              variant="outline"
              className="border-[hsl(var(--danger))]/50 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10"
              onClick={() => setResetMode(true)}
              data-testid="tenant-reset-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t("pages.settings.resetBtn", "Reset my workspace…")}
            </Button>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1 pt-1 border-t border-border">
              <ShieldAlert className="h-3 w-3" />{" "}
              {t(
                "pages.settings.resetHint",
                "You'll be asked to re-enter your password and type your workspace name."
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="mt-4">
        <TwoFactorCard />
      </div>
      <div className="mt-4">
        <RecentLoginsPanel />
      </div>
    </div>
  );
}
